import { useCallback, useEffect, useState } from "react";
import {
  GameConfiguration,
  GameProps,
} from "shared/games/types/GameConfiguration";
import { GameAnswer } from "shared/games/types/GameAnswers";
import {
  GameQuestion,
  getLooopFromGameQuestion,
} from "shared/games/types/GameQuestions";
import useOutOfLivesRedirect from "shared/user/lives/useOutOfLivesRedirect";
import AudioContextNeedsTapOverlay from "ui/atoms/AudioContextNeedsTapOverlay";
import { useAudioContextReady } from "util/audioContext";
import useAuth from "util/useAuth/useAuth";
import { State, isStateIncorrect, isStateCorrect } from "./types";
import SuccessScreen from "./SuccessScreen/SuccessScreen";
import { isLoggedIn } from "util/useAuth/types";
import useLoggedOutLiveTracker from "shared/user/lives/LoggedOutLiveTrackerContext/useLoggedOutLivesTracker";
import mixpanel from "mixpanel-browser";
import { questionAnswered } from "shared/analytics/mixpanel/gameEvents";
import Button from "ui/atoms/Button/Button";
import { ArrowRightCircleIcon } from "@heroicons/react/24/solid";
import { useRouter } from "next/navigation";
import GameWrapper from "./GameWrapper";
import useTodosContext from "shared/todos/useTodosContext";
import { getMusicianLink } from "shared/games/Musician";
import * as routes from "routes";

interface Props<Q, A> {
  question: Q;
  onRequestNext: () => void;
  navbarContextTitle: string | undefined;
  referenceTrackDisabled: boolean | undefined;
  __answerForTestingOnly?: A;
}

export default function createGame<
  Q extends GameQuestion,
  A extends GameAnswer
>({ FailureScreen, Game, onSubmit }: GameConfiguration<Q, A>) {
  return function G(props: Props<Q, A>) {
    const { back } = useRouter();
    const auth = useAuth();
    const [, todosDispatch] = useTodosContext();
    const [submitInProgress, setSubmitInProgress] = useState(false);
    const { markLifeLost: loggedOutMarkLifeLost } = useLoggedOutLiveTracker();
    const { question, __answerForTestingOnly, onRequestNext } = props;
    const looop = getLooopFromGameQuestion(question);
    const [audioCtxReady, setAudioCtxReady] = useAudioContextReady();
    const [gameState, setGameState] = useState<State<A>>(
      __answerForTestingOnly
        ? {
            type: "incorrect",
            answer: __answerForTestingOnly,
            pointsEarned: 0,
          }
        : { type: "game" }
    );

    useEffect(() => {
      setGameState({ type: "game" });
    }, [question]);

    const handleSubmitRequest: GameProps<Q, A>["onSubmit"] = useCallback(
      async (answer) => {
        setSubmitInProgress(true);
        let pointsEarned = 0;

        if (!isLoggedIn(auth)) {
          if (answer.correct) {
            console.log("adding signed out todo", question.todoId);
            if (question.todoId !== null) {
              todosDispatch({
                type: "SET_SINGLE_SIGNED_OUT_COMPLETED_TODO",
                payload: { completedTodoId: question.todoId },
              });
            }
          } else {
            loggedOutMarkLifeLost();
          }
        } else {
          const submitReturnValue = await onSubmit(question, answer, auth);

          pointsEarned = submitReturnValue.pointsEarned;

          if (submitReturnValue.user) {
            auth.replaceUser(submitReturnValue.user);
          }

          if (submitReturnValue.completedTodos) {
            todosDispatch({
              type: "SET_MULTIPLE_SIGNED_IN_COMPLETED_TODO",
              payload: {
                completedTodos: submitReturnValue.completedTodos,
              },
            });
          }
        }

        setSubmitInProgress(false);
        scrollTo(0, 0);

        if (answer.correct) {
          setGameState({ type: "correct", pointsEarned });
        } else {
          setGameState({ type: "incorrect", answer, pointsEarned });
        }

        // Track both logged in and logged out users
        mixpanel.track(questionAnswered.event, {
          [questionAnswered.properties.correct]: answer.correct,
          [questionAnswered.properties.type]: answer.type,
        });
      },
      [auth, loggedOutMarkLifeLost, question, todosDispatch]
    );

    return (
      <GameWrapper auth={auth} inGameContextTitle={props.navbarContextTitle}>
        {isStateIncorrect(gameState) ? (
          <FailureScreen
            mobileToolbarStyle="fixed"
            question={question}
            answer={gameState.answer}
            onLoaded={() => {
              scrollTo(0, 0);
            }}
            onNext={() => {
              onRequestNext();
            }}
          />
        ) : isStateCorrect(gameState) ? (
          <SuccessScreen
            mobileToolbarStyle="fixed"
            onNext={() => {
              onRequestNext();
            }}
          />
        ) : (
          <>
            {!audioCtxReady && (
              <AudioContextNeedsTapOverlay onClick={setAudioCtxReady} />
            )}

            <OutOfLivesRedirect />

            <Game
              referenceTrackDisabled={props.referenceTrackDisabled}
              submitInProgress={submitInProgress}
              mobileToolbarStyle="fixed"
              question={question}
              onSubmit={handleSubmitRequest}
              onLoaded={() => {
                scrollTo(0, 0);
              }}
            />
          </>
        )}

        <div className="mx-auto mt-2 max-w-6xl flex justify-between items-center">
          {looop.musician && looop.musician.name !== "Apple Loops" ? (
            <a
              href={getMusicianLink(looop.musician)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm leading-none"
            >
              <div className={"font-brik"}>Original music by</div>
              <strong className="underline underline-offset-2">
                {looop.musician.name}
              </strong>
            </a>
          ) : (
            <div />
          )}

          <Button
            theme="white"
            outline
            icon={(props) => <ArrowRightCircleIcon {...props} />}
            onClick={() => {
              back();
            }}
          >
            Exit
          </Button>
        </div>

        <div className="h-48" />
      </GameWrapper>
    );
  };
}

// This component only exists so we don't redirect users away on the success / fail page
function OutOfLivesRedirect() {
  const auth = useAuth();

  useOutOfLivesRedirect({ auth, url: routes.earTraining.mixing.home });

  return null;
}
